import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Loader2, MonitorSmartphone, Server, Shield, Plug } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { getToken } from '@/lib/auth'

type Status = { enabled?: boolean; vps_available?: boolean; connected?: boolean; state?: string; auth_mode?: string; error?: string }
export function BrowserPairingControl({ siteId, legacy }: { siteId: string; legacy: ReactNode }) {
  const [status, setStatus] = useState<Status | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [jobId, setJobId] = useState('')
  const [setupUrl, setSetupUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const endpoint = '/api/dashboard/wordpress-deep/sites/' + encodeURIComponent(siteId) + '/browser-connection'
  const refresh = useCallback(async () => {
    const response = await fetch(endpoint + (jobId ? '?job_id=' + encodeURIComponent(jobId) : ''), {
      headers: { Authorization: 'Bearer ' + (getToken() || '') },
    })
    const data = await response.json()
    if (!response.ok || data.success === false) throw new Error(data.error || 'Could not check the browser connection.')
    setStatus(data)
    setError('')
    if (data.state === 'connected') {
      setStatus({ ...data, connected: true }); setJobId(''); setBusy(false); setSetupUrl('')
    } else if (data.state === 'failed') {
      setError(data.error || 'Pairing did not complete.'); setJobId(''); setBusy(false)
    }
  }, [endpoint, jobId])
  useEffect(() => {
    let active = true
    refresh().catch((failure) => { if (active) setError(failure instanceof Error ? failure.message : 'Could not check the browser connection.') })
    const timer = window.setInterval(() => {
      refresh().catch((failure) => { if (active) setError(failure instanceof Error ? failure.message : 'Connection status is temporarily unavailable.') })
    }, jobId || setupUrl ? 3000 : 15000)
    return () => { active = false; window.clearInterval(timer) }
  }, [refresh, jobId, setupUrl])
  const act = async (target: 'vps' | 'local' | 'external' | 'disconnect') => {
    setError(''); setBusy(true); setCopied(false)
    // Open synchronously to avoid popup blocking after the authenticated API call.
    const tab = target === 'local' ? window.open('', '_blank') : null
    if (tab) tab.opener = null
    try {
      const response = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (getToken() || '') },
        body: JSON.stringify({ target }),
      })
      const data = await response.json()
      if (!response.ok || !data.success || data.enabled !== true) throw new Error(data.error || 'Secure pairing is not available yet.')
      if (target === 'disconnect') { setStatus({ enabled: true, connected: false }); setSetupUrl('') }
      else if (data.job_id) { setJobId(data.job_id); setStatus({ enabled: true, state: data.state }); return }
      else if (data.pairing_url) {
        const url = new URL(data.pairing_url)
        if (url.protocol !== 'https:') throw new Error('Invalid setup link.')
        setSetupUrl(url.href)
        if (tab) tab.location.href = url.href
      }
    } catch (failure) {
      if (tab) tab.close()
      setError(failure instanceof Error ? failure.message : 'Could not start pairing.')
    }
    setBusy(false)
  }
  // Preserve the installed workflow while the coordinated release is disabled.
  if (!status?.enabled) return <>{error && <p role="alert" className="text-xs text-danger">Secure browser status unavailable: {error}</p>}{legacy}</>
  return <Card className="ops-wp-card">
    <CardHeader className="ops-wp-card-header">
      <CardTitle className="flex items-center gap-2 text-sm">
        <Shield className="h-4 w-4" /> Browser Remote Control
        <span className="ml-auto text-xs font-normal">{status.connected ? 'Browser online' : busy ? 'Connecting…' : 'No browser online'}</span>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <p className="text-xs text-text-muted">Choose where OPS connects. Each browser is approved securely by your WordPress site.</p>
      {status.connected ? <>
        <p className="text-xs text-text-muted">{status.auth_mode === 'legacy-token' ? 'This browser uses the previous connection method.' : 'Your approved browser is connected.'}</p>
        <button onClick={() => act('disconnect')} disabled={busy} className="ops-wp-action ops-wp-action-danger">
          <Plug className="h-4 w-4" /> Disconnect browser
        </button>
      </> : <div className="flex flex-wrap gap-2">
        <button onClick={() => act('vps')} disabled={busy || !status.vps_available} title={status.vps_available ? undefined : 'VPS pairing is awaiting verification'} className="ops-wp-action">
          {busy && jobId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />} Connect VPS browser
        </button>
        <button onClick={() => act(mobile ? 'external' : 'local')} disabled={busy} className="ops-wp-action">
          <MonitorSmartphone className="h-4 w-4" /> {mobile ? 'Connect external browser' : 'Connect this browser'}
        </button>
        {!mobile && <button onClick={() => act('external')} disabled={busy} className="text-xs text-text-muted underline">Use a different computer</button>}
      </div>}
      {!status.vps_available && !status.connected && <p className="text-xs text-text-muted">VPS pairing is awaiting verification. You can connect your own browser now.</p>}
      {busy && jobId && <p role="status" className="text-xs text-text-muted">OPS is preparing and pairing the VPS browser. You can stay on this page.</p>}
      {setupUrl && !status.connected && <div className="space-y-2 rounded-lg border border-border p-3">
        <p className="text-xs">On the computer you want to connect, open the setup page, sign into WordPress if asked, and select <strong>Pair this browser</strong>. The OPS extension must be installed.</p>
        <input aria-label="Browser setup link" readOnly value={setupUrl} className="w-full rounded border border-border bg-surface-elevated p-2 text-xs" />
        <div className="flex gap-3">
          <button className="text-xs underline" onClick={async () => {
            try { await navigator.clipboard.writeText(setupUrl); setCopied(true) }
            catch { setError('Select and copy the setup link above.') }
          }}>{copied ? 'Copied' : 'Copy setup link'}</button>
          {!mobile && <a href={setupUrl} target="_blank" rel="noopener noreferrer" className="text-xs underline">Open setup page</a>}
        </div>
        <p className="text-xs text-text-muted">This link contains no password or connection key.</p>
      </div>}
      {error && <p role="alert" className="text-xs text-danger">{error}</p>}
    </CardContent>
  </Card>
}
